import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../api";

import { toasts } from "../components/AppToast";

import {
  FiTrash2,
  FiEdit,
  FiEye,
  FiPlus,
  FiSave,
  FiTool,
  FiClipboard,
  FiSearch,
} from "react-icons/fi";

import Layout from "../components/Layout";

const MAX_NO_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1);

const EMPTY_FORM = {
  optitem_id: "",
  opt_type: "",
  opt_max: 1,
  opt_dayrate: "",
  opt_maxrate: "",
};

export default function OptionalExtraDetail() {
  const navigate = useNavigate();

  const [optItems, setOptItems] = useState([]);
  const [data, setData] = useState([]);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    ...EMPTY_FORM,
  });

  const [editId, setEditId] = useState(null);

  const [formModal, setFormModal] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  const [delModal, setDelModal] = useState(null);
  const [delSaving, setDelSaving] = useState(false);

  // AUTH
  useEffect(() => {
    if (!localStorage.getItem("token")) {
      navigate("/login");
    }
  }, [navigate]);

  // LOAD OPTIONAL EXTRA ITEMS
  useEffect(() => {
    const loadOptionalExtraItems = async () => {
      try {
        const response = await API.get("/optional-extras/items");

        setOptItems(response.data || []);
      } catch (error) {
        console.error("Optional extra items error:", error);

        toasts.error(
          error.response?.data?.error ||
            error.response?.data?.message ||
            "Failed to load optional extra items"
        );
      }
    };

    loadOptionalExtraItems();
  }, []);

  // FETCH TABLE DATA
  const fetchData = useCallback(
    async (pg, q) => {
      setLoading(true);

      try {
        const response = await API.get("/optional-extras", {
          params: {
            page: pg,
            limit,
            search: q,
          },
        });

        const result = response.data || {};

        setData(result.data || []);
        setTotal(Number(result.total || 0));
        setTotalPages(Number(result.totalPages || 0));
      } catch (error) {
        console.error("fetchData error:", error);

        toasts.error(
          error.response?.data?.error ||
            error.response?.data?.message ||
            "Failed to load optional extra details"
        );

        setData([]);
        setTotal(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    },
    [limit]
  );

  // FETCH WHEN PAGE OR SEARCH CHANGES
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData(page, search);
    }, 300);

    return () => clearTimeout(timer);
  }, [page, search, fetchData]);

  // SEARCH
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  // FORM CHANGE
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((previous) => {
      const updated = {
        ...previous,
        [name]: value,
      };

      if (name === "opt_type" && value === "1") {
        updated.opt_dayrate = 1;
        updated.opt_maxrate = 1;
      }

      if (name === "opt_type" && value === "2") {
        updated.opt_dayrate = "";
        updated.opt_maxrate = "";
      }

      return updated;
    });
  };

  // RESET FORM
  const resetForm = () => {
    setForm({
      ...EMPTY_FORM,
    });

    setEditId(null);
  };

  // OPEN ADD MODAL
  const openAddModal = () => {
    resetForm();
    setFormModal(true);
  };

  // CLOSE FORM MODAL
  const closeFormModal = () => {
    if (saving) {
      return;
    }

    setFormModal(false);
    resetForm();
  };

  // EDIT
  const handleEdit = (row) => {
    setForm({
      optitem_id: String(row.optitem_id ?? ""),
      opt_type: String(row.opt_type ?? ""),
      opt_max: row.opt_max ?? 1,
      opt_dayrate: row.opt_dayrate ?? "",
      opt_maxrate: row.opt_maxrate ?? "",
    });

    setEditId(row.optdet_id);
    setFormModal(true);
  };

  // SUBMIT
  const handleSubmit = async () => {
    if (!form.optitem_id) {
      toasts.warning("Select an optional extra item");
      return;
    }

    if (!form.opt_type) {
      toasts.warning("Select a payment option");
      return;
    }

    if (!form.opt_max || Number(form.opt_max) <= 0) {
      toasts.warning("Maximum quantity must be greater than 0");
      return;
    }

    if (form.opt_type === "2") {
      if (form.opt_dayrate === "" || form.opt_dayrate === null) {
        toasts.warning("Enter Per Day Price");
        return;
      }

      if (form.opt_maxrate === "" || form.opt_maxrate === null) {
        toasts.warning("Enter Maximum Price");
        return;
      }

      if (Number(form.opt_dayrate) < 0) {
        toasts.warning("Per Day Price cannot be negative");
        return;
      }

      if (Number(form.opt_maxrate) < 0) {
        toasts.warning("Maximum Price cannot be negative");
        return;
      }
    }

    setSaving(true);

    try {
      const payload = {
        optitem_id: Number(form.optitem_id),
        opt_type: Number(form.opt_type),
        opt_max: Number(form.opt_max),
        opt_dayrate:
          form.opt_type === "2" ? Number(form.opt_dayrate) : 1,
        opt_maxrate:
          form.opt_type === "2" ? Number(form.opt_maxrate) : 1,
      };

      if (editId) {
        await API.put(`/optional-extras/${editId}`, payload);

        toasts.success("Optional extra updated successfully");
      } else {
        await API.post("/optional-extras", payload);

        toasts.success("Optional extra added successfully");
      }

      setFormModal(false);
      resetForm();

      await fetchData(page, search);
    } catch (error) {
      console.error("Submit error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Operation failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // DELETE
  const handleDelete = async () => {
    if (!delModal) {
      return;
    }

    setDelSaving(true);

    try {
      await API.delete(`/optional-extras/${delModal.optdet_id}`);

      toasts.success("Optional extra deleted successfully");

      setDelModal(null);

      if (data.length === 1 && page > 1) {
        setPage((previous) => previous - 1);
      } else {
        await fetchData(page, search);
      }
    } catch (error) {
      console.error("Delete error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Delete failed"
      );
    } finally {
      setDelSaving(false);
    }
  };

  // PAYMENT LABEL
  const paymentLabel = (type) => {
    if (type === 1 || type === "1") {
      return {
        label: "Free",
        className: "badge badge-success",
      };
    }

    if (type === 2 || type === "2") {
      return {
        label: "Paid",
        className: "badge badge-custom",
      };
    }

    return {
      label: "—",
      className: "badge bg-secondary",
    };
  };

  const isPaid = form.opt_type === "2";

  return (
    <Layout activePage="Optional Extra Details">
      <main className="dashboard-main">
        <div className="table-card">
          <div className="table-card-head">
            <h3 className="table-card-title d-flex align-items-center">
              <FiClipboard size={26} className="me-2" />
              <span>Manage Optional Extra Details</span>
            </h3>

            <div
              className="d-flex align-items-center gap-2"
              style={{
                flexWrap: "wrap",
              }}
            >
              <div className="search-wrapper">
                <span className="search-icon">
                  <FiSearch />
                </span>

                <input
                  type="text"
                  placeholder="Search optional extra..."
                  value={search}
                  onChange={handleSearch}
                  className="search-input"
                />

                {search && (
                  <button
                    type="button"
                    className="season-clear-btn"
                    onClick={() => {
                      setSearch("");
                      setPage(1);
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            <button
              type="button"
              className="um-btn-primary"
              onClick={openAddModal}
            >
              <span className="um-add-icon">＋</span>
              Add Optional Extra
            </button>
          </div>

          <div className="table-responsive">
            <table className="um-table">
              <thead>
                <tr className="um-table-head">
                  <th className="um-th text-center">#</th>
                  <th className="um-th text-center">
                    Optional Extra
                  </th>
                  <th className="um-th text-center">
                    Payment Option
                  </th>
                  <th className="um-th text-center">
                    Max No of Items
                  </th>
                  <th className="um-th text-center">
                    Per Day Price
                  </th>
                  <th className="um-th text-center">
                    Maximum Price
                  </th>
                  <th className="um-th text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 7 }).map(
                        (_, cellIndex) => (
                          <td
                            key={cellIndex}
                            className="um-td text-center"
                          >
                            <div className="skeleton" />
                          </td>
                        )
                      )}
                    </tr>
                  ))
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="empty-table-cell text-center py-5"
                    >
                      <div className="empty-icon">
                        <FiTool />
                      </div>

                      <div className="empty-title">
                        No optional extra details found
                      </div>

                      <div className="empty-sub">
                        Use the Add Optional Extra button to add one
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.map((row, index) => {
                    const payment = paymentLabel(row.opt_type);

                    return (
                      <tr
                        key={row.optdet_id}
                        className={`um-row ${
                          index % 2 === 0
                            ? "even-row"
                            : "odd-row"
                        }`}
                      >
                        <td className="um-td text-center">
                          {(page - 1) * limit + index + 1}
                        </td>

                        <td className="um-td text-center">
                          {row.optitem_name || "—"}
                        </td>

                        <td className="um-td text-center">
                          <span className={payment.className}>
                            {payment.label}
                          </span>
                        </td>

                        <td className="um-td text-center">
                          <span className="badge badge-success">
                            {row.opt_max ?? "-"}
                          </span>
                        </td>

                        <td className="um-td text-center">
                          {row.opt_dayrate ?? "—"}
                        </td>

                        <td className="um-td text-center">
                          {row.opt_maxrate ?? "—"}
                        </td>

                        <td className="um-td text-center">
                          <div className="action-buttons">
                            <button
                              type="button"
                              className="action-btn action-view"
                              title="View"
                              onClick={() => setViewItem(row)}
                            >
                              <FiEye size={15} />
                            </button>

                            <button
                              type="button"
                              className="action-btn action-edit"
                              title="Edit"
                              onClick={() => handleEdit(row)}
                            >
                              <FiEdit size={15} />
                            </button>

                            <button
                              type="button"
                              className="action-btn action-delete delete-enabled"
                              title="Delete"
                              onClick={() => setDelModal(row)}
                            >
                              <FiTrash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {totalPages > 0 && (
              <div className="pagination">
                <span className="pagin-info">
                  Showing {(page - 1) * limit + 1}
                  {" – "}
                  {Math.min(page * limit, total)}
                  {" of "}
                  {total}
                </span>

                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="pg-btn"
                    disabled={page === 1 || loading}
                    onClick={() =>
                      setPage((previous) =>
                        Math.max(1, previous - 1)
                      )
                    }
                  >
                    ← Prev
                  </button>

                  <span className="pagin-info">
                    Page {page} / {totalPages}
                  </span>

                  <button
                    type="button"
                    className="pg-btn"
                    disabled={
                      page >= totalPages || loading
                    }
                    onClick={() =>
                      setPage((previous) =>
                        Math.min(
                          totalPages,
                          previous + 1
                        )
                      )
                    }
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {formModal && (
          <div
            className="um-overlay"
            onClick={closeFormModal}
          >
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="um-modal-head">
                <div className="d-flex align-items-center gap-2">
                  <div className="form-card-icon">
                    {editId ? (
                      <FiEdit size={24} />
                    ) : (
                      <FiPlus size={24} />
                    )}
                  </div>

                  <div>
                    <h2 className="um-modal-title mb-0">
                      {editId
                        ? "Edit Optional Extra Details"
                        : "Add Optional Extra Details"}
                    </h2>

                    <p className="form-card-sub mb-0">
                      Configure optional extra payment and price
                      details
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  ✕
                </button>
              </div>

              <div className="um-modal-scroll">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="field-label">
                      Optional Extra Item *
                    </label>

                    <select
                      className="rd-input"
                      name="optitem_id"
                      value={form.optitem_id}
                      onChange={handleChange}
                    >
                      <option value="">
                        — Select Optional Extra —
                      </option>

                      {optItems.map((item) => (
                        <option
                          key={item.optitem_id}
                          value={item.optitem_id}
                        >
                          {item.optitem_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="field-label">
                      Payment Option *
                    </label>

                    <select
                      className="rd-input"
                      name="opt_type"
                      value={form.opt_type}
                      onChange={handleChange}
                    >
                      <option value="">— Select —</option>
                      <option value="1">Free</option>
                      <option value="2">Paid</option>
                    </select>
                  </div>

                  <div className="col-md-4">
                    <label className="field-label">
                      Maximum No of Items *
                    </label>

                    <select
                      className="rd-input"
                      name="opt_max"
                      value={form.opt_max}
                      onChange={handleChange}
                    >
                      {MAX_NO_OPTIONS.map((number) => (
                        <option
                          key={number}
                          value={number}
                        >
                          {number}
                        </option>
                      ))}
                    </select>
                  </div>

                  {isPaid && (
                    <>
                      <div className="col-md-6">
                        <label className="field-label">
                          Per Day Price *
                        </label>

                        <input
                          className="rd-input"
                          type="number"
                          name="opt_dayrate"
                          min="0"
                          step="0.01"
                          maxLength="7"
                          placeholder="Enter per day price"
                          value={form.opt_dayrate}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="field-label">
                          Maximum Price *
                        </label>

                        <input
                          className="rd-input"
                          type="number"
                          name="opt_maxrate"
                          min="0"
                          step="0.01"
                          maxLength="7"
                          placeholder="Enter maximum price"
                          value={form.opt_maxrate}
                          onChange={handleChange}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? (
                    <span className="spinner" />
                  ) : editId ? (
                    <>
                      <FiSave />
                      Update Details
                    </>
                  ) : (
                    <>
                      <FiPlus />
                      Add Details
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {viewItem && (
          <div
            className="um-overlay"
            onClick={() => setViewItem(null)}
          >
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="um-modal-head">
                <div>
                  <h2 className="um-modal-title mb-0">
                    <FiTool /> Optional Extra Details
                  </h2>

                  <p className="form-card-sub mb-0">
                    Optional extra configuration
                  </p>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={() => setViewItem(null)}
                >
                  ✕
                </button>
              </div>

              <div className="um-modal-scroll">
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="bg-light rounded-3 border p-3">
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Optional Extra
                      </div>

                      <div className="fw-bold">
                        {viewItem.optitem_name || "—"}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="bg-light rounded-3 border p-3">
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Payment Option
                      </div>

                      <div className="fw-bold">
                        {viewItem.opt_type === 1 ||
                        viewItem.opt_type === "1"
                          ? "Free"
                          : viewItem.opt_type === 2 ||
                            viewItem.opt_type === "2"
                          ? "Paid"
                          : "—"}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="bg-light rounded-3 border p-3">
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Max No of Items
                      </div>

                      <div className="fw-bold">
                        {viewItem.opt_max ?? "—"}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="bg-light rounded-3 border p-3">
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Per Day Price
                      </div>

                      <div className="fw-bold">
                        {viewItem.opt_dayrate ?? "—"}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="bg-light rounded-3 border p-3">
                      <div className="small text-muted text-uppercase fw-semibold mb-1">
                        Maximum Price
                      </div>

                      <div className="fw-bold">
                        {viewItem.opt_maxrate ?? "—"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={() => setViewItem(null)}
                >
                  Close
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={() => {
                    const item = viewItem;

                    setViewItem(null);

                    setTimeout(() => {
                      handleEdit(item);
                    }, 100);
                  }}
                >
                  <FiEdit />
                  Edit Details
                </button>
              </div>
            </div>
          </div>
        )}

        {delModal && (
          <div
            className="um-overlay"
            onClick={() => setDelModal(null)}
          >
            <div
              className="um-modal um-delete-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-content">
                <div className="delete-icon">
                  <FiTrash2 />
                </div>

                <h2 className="delete-title">
                  Delete Optional Extra?
                </h2>

                <div className="delete-badges">
                  <span className="badge badge-success">
                    {delModal.optitem_name ||
                      "Optional Extra"}
                  </span>

                  &nbsp;

                  <span className="badge bg-warning text-dark">
                    {delModal.opt_type === 1 ||
                    delModal.opt_type === "1"
                      ? "Free"
                      : delModal.opt_type === 2 ||
                        delModal.opt_type === "2"
                      ? "Paid"
                      : "—"}
                  </span>
                </div>

                <p className="delete-description">
                  This optional extra configuration will be
                  permanently deleted.
                  <br />
                  <strong>
                    This action cannot be undone.
                  </strong>
                </p>

                <div className="delete-actions">
                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={() => setDelModal(null)}
                    disabled={delSaving}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="delete-confirm-btn"
                    onClick={handleDelete}
                    disabled={delSaving}
                  >
                    {delSaving ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <FiTrash2 size={16} />
                        Delete Permanently
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

     
      </main>
    </Layout>
  );
}
